import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { createGlobalStyle } from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  InputAdornment,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Container,
  Grid,
  Card,
  CardActionArea,
  Divider,
  Breadcrumbs,
  Link,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  useMediaQuery,
  useTheme,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  PrintOutlined as PrintIcon,
  EventOutlined as EventIcon,
  PersonOutlined as PersonIcon,
  SearchOutlined as SearchIcon,
  NavigateNext as NavigateNextIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useAxios } from '../../hooks/use-axios';
import { baseUrls } from '../../constants';
import { useAuthContext } from '../../context/auth-context';
import AffiliationFilter from '../../components/reports/AffiliationFilter';
import { getAffiliationZones, filterByAffiliationZone } from '../../utils/report-filters';

const PrintStyles = createGlobalStyle`
  @media print {
    body * { visibility: hidden; }
    #printable-content, #printable-content * { visibility: visible; }
    #printable-content {
      position: absolute;
      left: 0; top: 0;
      width: 100%;
      padding: 0 10px;
    }
    table { width: 100% !important; font-size: 10px !important; }
    th, td { padding: 4px !important; font-size: 10px !important; word-wrap: break-word; }
    @page { size: landscape; margin: 0.5cm; }
  }
`;

const groupGradesByClub = (grades) => {
  const grouped = {};
  grades.forEach((grade) => {
    if (!grouped[grade.clubId]) {
      grouped[grade.clubId] = {
        clubName: grade.clubName,
        clubId: grade.clubId,
        affiliationName: grade.affiliationName,
        affiliationZone: grade.affiliationZone,
        grades: [],
        subtotal: 0,
      };
    }
    grouped[grade.clubId].grades.push(grade);
    grouped[grade.clubId].subtotal += grade.grade || 0;
  });
  return Object.values(grouped);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('es-GT', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const EventCard = ({ event, selected, onClick, disabled }) => (
  <Card
    onClick={disabled ? undefined : onClick}
    sx={{
      mb: 1.5,
      cursor: disabled ? 'default' : 'pointer',
      backgroundColor: selected ? '#C1EFD0' : 'background.paper',
      border: selected ? '1.5px solid #284B32' : '1.5px solid transparent',
      transition: 'background-color 0.15s, border-color 0.15s',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    <CardActionArea sx={{ p: 0 }} disabled={disabled}>
      <Box sx={{ p: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EventIcon sx={{ flexShrink: 0, color: selected ? '#284B32' : 'action.active', fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: selected ? 700 : 600, lineHeight: 1.3, color: selected ? '#284B32' : 'text.primary' }}>
            {event.name}
          </Typography>
        </Box>
        {event.count != null && (
          <Typography variant="caption" sx={{ color: 'text.disabled', ml: 3.5 }}>
            {event.count} calificación{event.count !== 1 ? 'es' : ''}
          </Typography>
        )}
      </Box>
    </CardActionArea>
  </Card>
);

export const GradesConfirmationByJudges = () => {
  const { activeCamp } = useAuthContext();
  const { post } = useAxios();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Step-level navigation lives in location.state so browser back/forward works.
  const ls = (location.state?.campId === activeCamp?._id) ? (location.state || {}) : {};
  const step            = ls.step            || 'judges';
  const selectedJudge   = ls.judge           || null;
  const judgeEvents     = ls.judgeEvents     || [];
  const judgeFilter     = ls.judgeFilter     || '';
  const depth           = ls.depth           || 0;
  const mobileDetailOpen = ls.mobileDetailOpen || false;

  // Local state: judges list and event-panel selections (no history needed for these).
  const [judges, setJudges]                   = useState([]);
  const [gradedFilter, setGradedFilter]       = useState('all'); // 'all' | 'graded' | 'pending'
  const [selectedDetailEvent, setSelectedDetailEvent] = useState(null);
  const [reportData, setReportData]           = useState(null);
  const [loadingJudges, setLoadingJudges]     = useState(false);
  const [loadingEvents, setLoadingEvents]     = useState(false);
  const [loadingReport, setLoadingReport]     = useState(false);

  useEffect(() => {
    if (activeCamp?._id) {
      fetchJudges(activeCamp._id);
      navigate('.', { replace: true, state: null });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCamp?._id]);

  // Reset detail panel when returning to a different judge's events list.
  useEffect(() => {
    setSelectedDetailEvent(null);
    setReportData(null);
  }, [selectedJudge?._id]);

  const fetchJudges = async (campId) => {
    setLoadingJudges(true);
    try {
      const [judgesRes, countsRes] = await Promise.all([
        axios.get(`${baseUrls.baseUrl}/reports/judges/${campId}`),
        axios.get(`${baseUrls.baseUrl}/reports/judgesWithEventCounts/${campId}`),
      ]);

      let allJudges = [];
      const raw = judgesRes?.data;
      if (raw?.users) allJudges = raw.users;
      else if (raw?.result) allJudges = raw.result;
      else if (raw?.judges) allJudges = raw.judges;
      else if (Array.isArray(raw)) allJudges = raw;

      const countsMap = {};
      (countsRes.data?.judges || []).forEach(j => { countsMap[j._id] = j.eventCount; });

      allJudges.sort((a, b) => (a.fullname || '').toLowerCase().localeCompare((b.fullname || '').toLowerCase()));
      setJudges(allJudges.map(j => ({ ...j, eventCount: countsMap[j._id] ?? 0 })));
    } catch (error) {
      if (error.response?.status !== 404) console.error('Error fetching judges:', error);
    } finally {
      setLoadingJudges(false);
    }
  };

  const handleFilterChange = (value) => {
    navigate('.', {
      replace: true,
      state: { campId: activeCamp._id, step: 'judges', judgeFilter: value, depth: 0 },
    });
  };

  const handleSelectJudge = async (judge) => {
    setLoadingEvents(true);
    try {
      const response = await post(`${baseUrls.baseUrl}/reports/eventsByJudge`, {
        campId: activeCamp._id,
        userId: judge._id,
      });
      const eventsData = response.data?.events || [];
      navigate('.', {
        state: {
          campId: activeCamp._id,
          step: 'events',
          judge,
          judgeEvents: eventsData,
          judgeFilter,
          depth: depth + 1,
        },
      });
    } catch (error) {
      console.error('Error fetching judge events:', error);
      alert('Error al obtener los eventos del juez');
    } finally {
      setLoadingEvents(false);
    }
  };

  const handleSelectDetailEvent = async (event) => {
    const alreadySelected = selectedDetailEvent?._id === event._id;

    if (isMobile) {
      // Push a history entry so the browser back button returns to the events list.
      navigate('.', {
        state: { ...ls, campId: activeCamp._id, mobileDetailOpen: true },
      });
      if (alreadySelected) return;
    }

    if (alreadySelected) return;
    setSelectedDetailEvent(event);
    setReportData(null);
    setLoadingReport(true);
    try {
      const response = await post(`${baseUrls.baseUrl}/reports/gradesByUsersAndCamp`, {
        campId: activeCamp._id,
        eventId: event._id,
        userId: selectedJudge._id,
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      alert('Error al generar el reporte');
    } finally {
      setLoadingReport(false);
    }
  };

  const goToJudges = () => navigate(-depth);

  const filteredJudges = judges.filter((j) => {
    if (!(j.fullname || '').toLowerCase().includes(judgeFilter.toLowerCase())) return false;
    if (gradedFilter === 'graded')  return (j.eventCount ?? 0) > 0;
    if (gradedFilter === 'pending') return (j.eventCount ?? 0) === 0;
    return true;
  });

  if (!activeCamp?._id) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', mt: 4 }}>
        <Typography color="text.secondary">
          Seleccione un campamento activo para ver el reporte
        </Typography>
      </Box>
    );
  }

  const isEventsStep = step === 'events';

  return (
    <>
      <PrintStyles />
      <Container maxWidth="xl" sx={{
        pt: 3, pb: 2,
        ...(isEventsStep && !isMobile ? { height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' } : { p: { xs: 2, sm: 3 } }),
      }}>
        {/* Header + breadcrumb */}
        <Box sx={{ mb: 3, flexShrink: 0, '@media print': { display: 'none' } }}>
          <Box sx={{ pl: 2, borderLeft: '4px solid #284B32', mb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2, fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
              Confirmación de Calificaciones por Jueces
            </Typography>
          </Box>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mt: 1 }}>
            {step !== 'judges' ? (
              <Link
                component="button"
                underline="hover"
                color="inherit"
                onClick={goToJudges}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, background: 'none', border: 'none', p: 0, font: 'inherit', cursor: 'pointer' }}
              >
                <PersonIcon fontSize="small" />
                Jueces
              </Link>
            ) : (
              <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PersonIcon fontSize="small" />
                Jueces
              </Typography>
            )}
            {selectedJudge && (
              <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, maxWidth: { xs: 160, sm: 'none' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                <PersonIcon fontSize="small" color="secondary" />
                {selectedJudge.fullname}
              </Typography>
            )}
          </Breadcrumbs>
        </Box>

        {/* ── Step: Jueces ── */}
        {step === 'judges' && (
          <>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 2 }}>
              <TextField
                size="small"
                placeholder="Filtrar por nombre..."
                value={judgeFilter}
                onChange={(e) => handleFilterChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: { xs: '100%', sm: 320 } }}
              />
              <ToggleButtonGroup
                value={gradedFilter}
                exclusive
                onChange={(_, val) => { if (val) setGradedFilter(val); }}
                size="small"
                sx={{ flexWrap: 'wrap' }}
              >
                <ToggleButton value="all">Todos</ToggleButton>
                <ToggleButton value="graded">Con eventos</ToggleButton>
                <ToggleButton value="pending">Sin eventos</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {loadingJudges || loadingEvents ? (
              <Box display="flex" justifyContent="center" mt={6}>
                <CircularProgress sx={{ color: '#284B32' }} />
              </Box>
            ) : filteredJudges.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" mt={4}>
                {judges.length === 0 ? 'No hay jueces disponibles' : 'No se encontraron jueces con ese nombre'}
              </Typography>
            ) : (
              <Paper variant="outlined" sx={{ maxWidth: { xs: '100%', sm: 640 } }}>
                <Typography variant="caption" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block' }}>
                  {filteredJudges.length} jue{filteredJudges.length !== 1 ? 'ces' : ''}
                </Typography>
                <Divider />
                <List disablePadding>
                  {filteredJudges.map((judge, index) => (
                    <React.Fragment key={judge._id}>
                      <ListItemButton
                        onClick={() => handleSelectJudge(judge)}
                        sx={{ py: 1.5, px: 2 }}
                      >
                        <PersonIcon color="action" sx={{ mr: 1.5, flexShrink: 0 }} />
                        <ListItemText
                          primary={judge.fullname}
                          primaryTypographyProps={{ fontWeight: 600 }}
                        />
                        <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={`${judge.eventCount ?? 0} evento${(judge.eventCount ?? 0) !== 1 ? 's' : ''}`}
                            size="small"
                            sx={
                              (judge.eventCount ?? 0) > 0
                                ? { backgroundColor: '#E6F4EA', color: '#1b5e20', fontWeight: 700 }
                                : { backgroundColor: '#f5f5f5', color: '#9e9e9e', fontWeight: 600 }
                            }
                          />
                          <ChevronRightIcon fontSize="small" color="action" />
                        </ListItemSecondaryAction>
                      </ListItemButton>
                      {index < filteredJudges.length - 1 && <Divider component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}

        {/* ── Step: Eventos del juez ── */}
        {step === 'events' && (
          isMobile ? (
            /* ── Mobile: drill-down list → detail ── */
            mobileDetailOpen ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {/* Back button */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, '@media print': { display: 'none' } }}>
                  <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: '#284B32' }}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#284B32' }}>
                    Volver a eventos
                  </Typography>
                </Box>
                {loadingReport ? (
                  <Box display="flex" justifyContent="center" mt={6}>
                    <CircularProgress sx={{ color: '#284B32' }} />
                  </Box>
                ) : reportData ? (
                  <ReportDetailPanel
                    judge={selectedJudge}
                    event={selectedDetailEvent}
                    reportData={reportData}
                    activeCamp={activeCamp}
                    isMobile
                  />
                ) : null}
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600 }}>
                  {judgeEvents.length} evento{judgeEvents.length !== 1 ? 's' : ''} evaluado{judgeEvents.length !== 1 ? 's' : ''}
                </Typography>
                {judgeEvents.length === 0 ? (
                  <Typography color="text.secondary" textAlign="center" mt={4}>
                    Este juez no ha evaluado ningún evento aún
                  </Typography>
                ) : (
                  judgeEvents.map(event => (
                    <EventCard
                      key={event._id}
                      event={event}
                      selected={selectedDetailEvent?._id === event._id}
                      onClick={() => handleSelectDetailEvent(event)}
                      disabled={loadingReport}
                    />
                  ))
                )}
              </Box>
            )
          ) : (
            /* ── Desktop: split layout ── */
            <Grid container spacing={3} sx={{ flex: 1, minHeight: 0, '@media print': { display: 'block' } }}>
              {/* Left: events list */}
              <Grid item xs={12} md={4} sx={{ height: '100%', '@media print': { display: 'none' } }}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1.5, fontWeight: 600, flexShrink: 0 }}>
                    {judgeEvents.length} evento{judgeEvents.length !== 1 ? 's' : ''} evaluado{judgeEvents.length !== 1 ? 's' : ''}
                  </Typography>
                  {judgeEvents.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" mt={4}>
                      Este juez no ha evaluado ningún evento aún
                    </Typography>
                  ) : (
                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                      {judgeEvents.map(event => (
                        <EventCard
                          key={event._id}
                          event={event}
                          selected={selectedDetailEvent?._id === event._id}
                          onClick={() => handleSelectDetailEvent(event)}
                          disabled={loadingReport}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Right: report detail */}
              <Grid item xs={12} md={8} sx={{ height: '100%' }}>
                <Card sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                  {!selectedDetailEvent ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <Typography color="text.disabled">Selecciona un evento para ver el detalle.</Typography>
                    </Box>
                  ) : loadingReport ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <CircularProgress sx={{ color: '#284B32' }} />
                    </Box>
                  ) : reportData ? (
                    <ReportDetailPanel
                      judge={selectedJudge}
                      event={selectedDetailEvent}
                      reportData={reportData}
                      activeCamp={activeCamp}
                    />
                  ) : null}
                </Card>
              </Grid>
            </Grid>
          )
        )}
      </Container>
    </>
  );
};

const ReportDetailPanel = ({ judge, event, reportData, activeCamp, isMobile }) => {
  const [zoneFilter, setZoneFilter] = useState('');

  const allClubGroups = groupGradesByClub(reportData.gradedByUser);
  const zones = getAffiliationZones(allClubGroups);
  const clubGroups = filterByAffiliationZone(allClubGroups, zoneFilter);
  // Con filtro activo los totales deben reflejar sólo lo visible
  const visibleCount = zoneFilter
    ? clubGroups.reduce((sum, g) => sum + g.grades.length, 0)
    : reportData.count;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : '100%', overflow: isMobile ? 'visible' : 'hidden' }}>
      {/* Panel header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2, flexShrink: 0, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            {event.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {visibleCount} calificación{visibleCount !== 1 ? 'es' : ''} guardadas por {judge?.fullname}
            {zoneFilter ? ` · ${zoneFilter}` : ''}
          </Typography>
        </Box>
        {zones.length > 0 && (
          <Box sx={{ minWidth: 200, flexShrink: 0 }}>
            <AffiliationFilter
              value={zoneFilter}
              onChange={setZoneFilter}
              zones={zones}
              size="small"
            />
          </Box>
        )}
        <Button
          variant="contained"
          size="small"
          startIcon={<PrintIcon />}
          onClick={() => window.print()}
          sx={{ backgroundColor: '#284B32', '&:hover': { backgroundColor: '#1a3620' }, flexShrink: 0 }}
        >
          Imprimir
        </Button>
      </Box>

      <Divider sx={{ mb: 2, flexShrink: 0 }} />

      {/* Printable content */}
      <Box id="printable-content" sx={{ flex: 1, overflow: isMobile ? 'visible' : 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Print-only header */}
        <Box sx={{ display: 'none', textAlign: 'center', mb: 2, '@media print': { display: 'block' } }}>
          <Typography variant="h4" gutterBottom>Confirmación de Calificaciones por Jueces</Typography>
          <Typography variant="h6" gutterBottom>{activeCamp?.name || ''}</Typography>
          <Typography variant="subtitle1" gutterBottom>
            {judge?.fullname || ''} — {event?.name || ''}
          </Typography>
          <Typography variant="body1" gutterBottom>Total de Calificaciones: {visibleCount}</Typography>
          {zoneFilter && (
            <Typography variant="body2" gutterBottom>Afiliación: {zoneFilter}</Typography>
          )}
        </Box>

        {isMobile ? (
          /* ── Mobile: acordeón por club ── */
          <Box>
            {clubGroups.map((clubGroup) => (
              <Accordion key={clubGroup.clubId} defaultExpanded disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#f9f9f9', minHeight: 48, '&.Mui-expanded': { minHeight: 48 } }}>
                  <Box sx={{ flex: 1, mr: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#284B32', lineHeight: 1.2 }}>
                      {clubGroup.clubName}
                    </Typography>
                    {(clubGroup.grades[0]?.affiliationName || clubGroup.grades[0]?.affiliationZone) && (
                      <Typography variant="caption" color="text.secondary">
                        {[clubGroup.grades[0]?.affiliationName, clubGroup.grades[0]?.affiliationZone].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`Subtotal: ${clubGroup.subtotal}`}
                    size="small"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 700, mr: 1, flexShrink: 0 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {clubGroup.grades.map((grade, index) => (
                    <React.Fragment key={grade._id}>
                      {index > 0 && <Divider />}
                      <Box sx={{ px: 2, py: 1.25 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                            {grade.rubricName}
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#284B32', flexShrink: 0 }}>
                            {grade.grade}<Typography component="span" variant="caption" color="text.secondary"> / {grade.maxPoints}</Typography>
                          </Typography>
                        </Box>
                        {grade.comments && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25, fontStyle: 'italic' }}>
                            {grade.comments}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.25 }}>
                          {formatDate(grade.savedDate)}
                        </Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        ) : (
          /* ── Desktop: tabla ── */
          <TableContainer component={Paper} sx={{ flex: 1, overflowY: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Club</TableCell>
                  <TableCell>Afiliación</TableCell>
                  <TableCell>Zona</TableCell>
                  <TableCell>Rubro</TableCell>
                  <TableCell align="right">Máx.</TableCell>
                  <TableCell align="right">Calif.</TableCell>
                  <TableCell>Comentarios</TableCell>
                  <TableCell>Fecha</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clubGroups.map((clubGroup) => (
                  <React.Fragment key={clubGroup.clubId}>
                    {clubGroup.grades.map((grade, index) => (
                      <TableRow key={grade._id}>
                        <TableCell>{index === 0 ? <strong>{grade.clubName}</strong> : ''}</TableCell>
                        <TableCell>{index === 0 ? (grade.affiliationName || '-') : ''}</TableCell>
                        <TableCell>{index === 0 ? (grade.affiliationZone || '-') : ''}</TableCell>
                        <TableCell>{grade.rubricName}</TableCell>
                        <TableCell align="right">{grade.maxPoints}</TableCell>
                        <TableCell align="right">{grade.grade}</TableCell>
                        <TableCell>{grade.comments || '-'}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(grade.savedDate)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow
                      sx={{
                        backgroundColor: '#e3f2fd',
                        '@media print': { backgroundColor: '#e3f2fd !important', WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' },
                      }}
                    >
                      <TableCell colSpan={5} align="right" sx={{ fontWeight: 600 }}>
                        Subtotal {clubGroup.clubName}:
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{clubGroup.subtotal}</TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};
